import { collection, setDoc, doc, getDocs } from "firebase/firestore";

import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, db } from "../firebase/firebase-config";
import { APP_URL } from "../constants";
import { httpClient } from "../utils/http-client";

const createBoUser = async ({
  email,
  password,
  role,
  setLoading,
  signUpError,
  signUpSuccess,
}) => {
  setLoading(true);
  await createUserWithEmailAndPassword(auth, email, password)
    .then((userCredential) => {
      // SEND EMAIL VERIFICATION ON CREATING USER
      sendEmailVerification(userCredential.user)
        .then(async () => {
          let userInfo = {
            firstName: "",
            lastName: "",
            email: email,
            uid: userCredential.user.uid,
            role: role,
            isActive: true,
            phone: "",
            location: {
              address: "",
              country: "",
            },
            sex: "",
          };
          //   store usser on  firestores
          await setDoc(
            doc(db, "back_office_users", userCredential.user.uid),
            userInfo
          );

          signUpSuccess("User Created Successfuly");
          setLoading(false);
        })
        .catch((error) => signUpError(error));
    })
    .catch((error) => signUpError(error))
    .finally(() => setLoading(false));
};

const resetPassword = (email) => sendPasswordResetEmail(auth, email);

const fetchUsers = (token, { offset = 0, limit = 10 } = {}) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(Number(offset) || 0));
    params.set("size", String(Number(limit) || 10));
    return httpClient.get(`${APP_URL}/v1/user/users?${params.toString()}`, {
      Authorization: `Bearer ${token}`,
    });
  } catch (error) {
    console.log("Error getting users", error);
  }
};

const fetchUserDetails = (id, token) => {
  try {
    return httpClient.get(`${APP_URL}/v1/user/${id}`, {
      Authorization: `Bearer ${token}`,
      locale: navigator.language,
    });
  } catch (error) {
    console.log("Error getting user info", error);
  }
};

const fetchUserTransactions = (id, token, { offset = 0, limit = 10 } = {}) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(Number(offset) || 0));
    params.set("size", String(Number(limit) || 10));
    return httpClient.get(
      `${APP_URL}/v1/user/transactions/${id}?${params.toString()}`,
      {
        Authorization: `Bearer ${token}`,
        locale: navigator.language,
      }
    );
  } catch (error) {
    console.log("Error getting user transactions", error);
  }
};

const updateUser = (id, payLoad, token) => {
  console.log(payLoad);
  try {
    return httpClient.put(APP_URL + `/v1/user/edit/${id}`, payLoad, {
      Authorization: `Bearer ${token}`,
      locale: navigator.language,
    });
  } catch (error) {
    console.log("Error updating user", error);
  }
};

const toggleUserStatus = (id, payload, token) => {
  // payload: { status: "activate" | "suspend" }.
  if (!payload.status) throw new Error("Error updating user status");

  try {
    return httpClient.put(APP_URL + `/v1/user/status/${id}`, payload, {
      Authorization: `Bearer ${token}`,
      locale: navigator.language,
    });
  } catch (error) {
    console.log("Error updating user status", error);
  }
};

const getMerchantIpWhitelist = (uid, token) => {
  try {
    return httpClient.get(`${APP_URL}/v1/user/ip-whitelist/${uid}`, {
      Authorization: `Bearer ${token}`,
      locale: navigator.language,
    });
  } catch (error) {
    console.log("Error getting merchant IP whitelist", error);
  }
};

const fetchMerchantLedgerBalances = (uid, token) => {
  try {
    return httpClient.get(`${APP_URL}/v1/user/ledger-balances/${uid}`, {
      Authorization: `Bearer ${token}`,
      locale: navigator.language,
    });
  } catch (error) {
    console.log("Error getting merchant ledger balances", error);
  }
};

const adjustMerchantLedger = (uid, data, token) => {
  try {
    return httpClient.postFile(
      `${APP_URL}/v1/user/ledger-adjust/${uid}`,
      data,
      {
        Authorization: `Bearer ${token}`,
        locale: navigator.language,
      }
    );
  } catch (error) {
    console.log("Error adjusting merchant ledger", error);
    return error;
  }
};

const updateMerchantKyb = (uid, payload, token) => {
  try {
    return httpClient.put(`${APP_URL}/v1/user/kyb/${uid}`, payload, {
      Authorization: `Bearer ${token}`,
      locale: navigator.language,
    });
  } catch (error) {
    console.log("Error updating merchant KYB", error);
  }
};

const setMerchantIpWhitelist = (uid, ips, token) => {
  try {
    return httpClient.put(
      `${APP_URL}/v1/user/ip-whitelist/${uid}`,
      { ips },
      {
        Authorization: `Bearer ${token}`,
        locale: navigator.language,
      }
    );
  } catch (error) {
    console.log("Error updating merchant IP whitelist", error);
  }
};

const setPin = async (pinUpdate) => {
  try {
    const data = {
      pin: pinUpdate.pin,
      confirm_pin: pinUpdate.rePin,
    };
    // console.log(`the pin data sending ${data.pin} ${data.confirm_pin} ${pinUpdate.id}`);

    return httpClient.post(APP_URL + `/v1/user/set-pin/${pinUpdate.id}`, data);
  } catch (error) {
    console.log("Error Setting PIN", error);
    return error;
  }
};

const updatePin = async (pinUpdate) => {
  try {
    const data = {
      newPin: pinUpdate.pin,
      newPinConfirm: pinUpdate.confirmPin,
      ...pinUpdate,
    };
    // console.log(`the pin data sending ${data.pin} ${data.confirm_pin} ${pinUpdate.id}`);

    return httpClient.put(APP_URL + `/v1/user/up-pin/${pinUpdate.id}`, data);
  } catch (error) {
    console.log("Error Setting PIN", error);
    return error;
  }
};

const signup = async (userInfo) => {
  try {
    const userCredentials = {
      email: userInfo.email,
      first_name: userInfo.first_name,
      last_name: userInfo.last_name,
      phone_number: userInfo.phone_number,
      address: userInfo.address,
      city: userInfo.city,
      country: userInfo.country,
      role: userInfo.role,
      language: userInfo.language,
      pin: Number(userInfo.pin),
      confirm_pin: Number(userInfo.confirm_pin),
      image: userInfo.image,
      terms_privacy: userInfo.terms_privacy.toString(),
      business_registration_number: userInfo.business_registration_number,
      business_registration_expiry: userInfo.business_registration_expiry,
      business_registration_doc: userInfo.business_registration_doc,
      md_name: userInfo.md_name,
      md_id_expiry: userInfo.md_id_expiry,
      md_selfie_doc: userInfo.md_selfie_doc,
      poa_document_date: userInfo.poa_document_date,
      proof_of_address_doc: userInfo.proof_of_address_doc,
      tax_id: userInfo.tax_id,
    };
    const headers = new Headers();
    headers.append("locale", navigator.language);
    return await httpClient.postFile(
      `${APP_URL}/v1/user/signup`,
      userCredentials,
      headers
    );
  } catch (error) {
    console.log("Error Signing up user", error);
    return "hello";
  }
};

const chatSignup = async (userInfo) => {
  try {
    return httpClient.post(
      APP_URL + `/v1/user/bot-signup/${userInfo.id}`,
      userInfo
    );
  } catch (error) {
    console.log("Error bot Signing up user", error);
    return error;
  }
};

const verifyKyc = async (userInfo) => {
  try {
    return httpClient.post(
      APP_URL + `/v1/user/kyc-verify/${userInfo.token}`,
      userInfo
    );
  } catch (error) {
    console.log("Error. There's been an issue verifying your information. Please try again", error);
    return error;
  }
};

const verifyAddress = async (userInfo) => {
  try {
    return httpClient.post(
      APP_URL + `/v1/user/kyc-address/${userInfo.token}`,
      userInfo
    );
  } catch (error) {
    console.log("Error. There's been an issue verifying your address document. Please try again", error);
    return error;
  }
};

const verifyProfession = async (userInfo) => {
  try {
    return httpClient.post(
      APP_URL + `/v1/user/kyc-profession/${userInfo.token}`,
      userInfo
    );
  } catch (error) {
    console.log("Error. There's been an issue verifying your address document. Please try again", error);
    return error;
  }
};

const getUserInfo = async (token) => {
  try {
    return httpClient.get(
      APP_URL + `/v1/user/info/${token}`,
    );
  } catch (error) {
    console.log("Error. There's been an issue getting your user information. Please reload again", error);
    return error;
  }
};

const updateLanguage = async (updateData) => {
  try {
    const data = {
      language: updateData.language,
      pin: updateData.pin,
    };
    return httpClient.put(APP_URL + `/v1/user/up-lang/${updateData.id}`, data);
  } catch (error) {
    console.log("Error updating language", error);
    return error;
  }
};

const fetchWallet = async (updateData) => {
  try {
    const data = {
      pin: updateData.pin,
    };
    return httpClient.get(APP_URL + `/v1/user/balances/${updateData.id}`, data);
  } catch (error) {
    console.log("Error getting wallets", error);
    return error;
  }
};

const fetchAccountInfo = async (updateData) => {
  try {
    const data = {
      pin: updateData.pin,
    };
    return httpClient.get(APP_URL + `/v1/user/profile/${updateData.id}`, data);
  } catch (error) {
    console.log("Error getting wallets", error);
    return error;
  }
};

export {
  fetchUsers,
  fetchUserDetails,
  fetchUserTransactions,
  updateUser,
  toggleUserStatus,
  getMerchantIpWhitelist,
  setMerchantIpWhitelist,
  updateMerchantKyb,
  fetchMerchantLedgerBalances,
  adjustMerchantLedger,
  createBoUser,
  resetPassword,
  setPin,
  fetchWallet,
  fetchAccountInfo,
  updateLanguage,
  signup,
  chatSignup,
  updatePin,
  verifyKyc,
  verifyAddress,
  verifyProfession,
  getUserInfo
};
